import { getGestureActionValue } from "./action-value.js";
import { isInteractionLocked } from "../ui/interaction-lock.js";
import { resolveRegion } from "./region.js";
import { isInteractiveTarget, listen } from "@videojs/utils/dom";
//#region src/dom/gesture/coordinator.ts
const TAP_THRESHOLD = 250;
var GestureCoordinator = class {
	#target;
	#bindings = [];
	#recognizers = /* @__PURE__ */ new Set();
	#disconnect = null;
	#subscribers = /* @__PURE__ */ new Set();
	constructor(target) {
		this.#target = target;
	}
	get bindings() {
		return this.#bindings;
	}
	subscribe(callback) {
		this.#subscribers.add(callback);
		return () => this.#subscribers.delete(callback);
	}
	/**
	* Whether a registered binding claims this tap for the given action. A claimed tap belongs to the gesture layer, so
	* callers should leave it alone. Taps on interactive targets (buttons, sliders) are never claimed — the same
	* filtering the pointerup listener applies. A disabled binding still claims: disabling a gesture opts out of the
	* action, it doesn't hand the tap back to a fallback handler.
	*/
	claimsTap(event, action) {
		if (isInteractionLocked(this.#target)) return true;
		if (isInteractiveTarget(event)) return false;
		return this.#bindings.some((b) => b.type === "tap" && b.action === action && (!b.pointer || b.pointer === event.pointerType));
	}
	add(binding) {
		const value = getGestureActionValue(binding.action ?? "", binding.region, binding.value);
		const wrapped = {
			...binding,
			value,
			onActivate: (event) => {
				if (this.#subscribers.size > 0) {
					const activateEvent = {
						type: binding.type,
						source: "gesture",
						action: binding.action,
						value,
						region: binding.region,
						pointer: binding.pointer,
						event
					};
					for (const cb of this.#subscribers) try {
						cb(activateEvent);
					} catch (error) {
						console.warn("[vjs-gesture] subscribe callback threw:", error);
					}
				}
				binding.onActivate(event);
			}
		};
		this.#bindings.push(wrapped);
		this.#recognizers.add(wrapped.recognizer);
		this.#connect();
		let removed = false;
		return () => {
			if (removed) return;
			removed = true;
			const idx = this.#bindings.indexOf(wrapped);
			if (idx !== -1) this.#bindings.splice(idx, 1);
			this.#maybeDisconnect();
		};
	}
	#connect() {
		if (this.#disconnect) return;
		this.#disconnect = new AbortController();
		const { signal } = this.#disconnect;
		let pointerDownTime = 0;
		listen(this.#target, "pointerdown", (event) => {
			if (isInteractionLocked(this.#target)) {
				pointerDownTime = 0;
				return;
			}
			if (event.button !== 0) return;
			pointerDownTime = Date.now();
		}, { signal });
		listen(this.#target, "pointerup", (event) => {
			if (isInteractionLocked(this.#target)) {
				pointerDownTime = 0;
				return;
			}
			if (event.button !== 0) return;
			if (Date.now() - pointerDownTime > TAP_THRESHOLD) return;
			if (isInteractiveTarget(event)) return;
			const pointerType = event.pointerType;
			const clientX = event.clientX;
			const target = this.#target;
			const bindings = this.#bindings;
			const matches = { resolve: (type) => matchBindings(bindings, type, pointerType, clientX, target) };
			for (const recognizer of this.#recognizers) recognizer.handleUp(matches, event);
		}, { signal });
	}
	#maybeDisconnect() {
		if (this.#bindings.length > 0) return;
		for (const recognizer of this.#recognizers) recognizer.reset();
		this.#recognizers.clear();
		this.#disconnect?.abort();
		this.#disconnect = null;
	}
};
const coordinators = /* @__PURE__ */ new WeakMap();
/** Look up the gesture coordinator for a target element, if one exists. */
function findGestureCoordinator(target) {
	return coordinators.get(target);
}
function getGestureCoordinator(target) {
	let coordinator = coordinators.get(target);
	if (!coordinator) {
		coordinator = new GestureCoordinator(target);
		coordinators.set(target, coordinator);
	}
	return coordinator;
}
function matchBindings(bindings, type, pointerType, clientX, target) {
	const rect = target.getBoundingClientRect();
	const activeRegions = getActiveRegions(bindings, type, pointerType);
	const region = activeRegions.size > 0 ? resolveRegion(clientX, rect, activeRegions) : null;
	const matches = [];
	for (const binding of bindings) {
		if (binding.disabled) continue;
		if (binding.type !== type) continue;
		if (binding.pointer && binding.pointer !== pointerType) continue;
		if (binding.region) {
			if (binding.region !== region) continue;
		} else if (region !== null) continue;
		matches.push(binding);
	}
	return matches;
}
function getActiveRegions(bindings, type, pointerType) {
	const regions = /* @__PURE__ */ new Set();
	for (const binding of bindings) {
		if (binding.disabled) continue;
		if (binding.type !== type) continue;
		if (binding.pointer && binding.pointer !== pointerType) continue;
		if (binding.region) regions.add(binding.region);
	}
	return regions;
}
//#endregion
export { GestureCoordinator, findGestureCoordinator, getGestureCoordinator };

//# sourceMappingURL=coordinator.js.map